// components/Chatbot/ChatMinimizedPill.tsx
// UX-R1 task 3.1: when the AI assistant is disabled on desktop, the chat
// column collapses and this floating pill takes its place — a re-engagement
// hook, not a void. Hover/focus reveals the two highest-value AI verbs plus
// the enable action. The (hidden) ChatInterface stays mounted, so the
// quick actions can dispatch the standard `askChatbotHelp` event first and
// then enable — ChatInterface's pending-help-request effect auto-sends the
// message the moment the assistant flips on.
//
// Touch pointers (>=lg touchscreens: iPad landscape, touch laptops) never fire
// hover, so there the pill is a TOGGLE: first tap reveals the actions —
// including an explicit Enable button, since the pill itself no longer enables
// on the first tap — and outside-tap/Escape collapses it again. Mouse/keyboard
// behavior is unchanged.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardCheck, Sparkles } from 'lucide-react';

import { useChatbot } from '../../contexts/ChatbotContext';
import { useMobile } from '../../contexts/MobileContext';
import { dispatchAskChatbotHelp } from '../../utils/appEvents';

const dispatchChatbotHelp = (message: string, action: string): void => {
  dispatchAskChatbotHelp({ message, action });
};

const ACTION_BUTTON_CLASSES =
  'flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-full ' +
  'bg-surface-card text-ink border border-line-soft shadow-md ' +
  'hover:bg-surface-sunken transition-colors';

const REVEAL_BASE =
  'flex flex-col items-end gap-2 transition-all duration-base ease-out-soft';
const REVEAL_HIDDEN = 'opacity-0 translate-y-1 pointer-events-none';
const REVEAL_VISIBLE = 'opacity-100 translate-y-0 pointer-events-auto';
const REVEAL_ON_HOVER =
  'group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto ' +
  'group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto';

const ChatMinimizedPill = () => {
  const { setEnabled, isLoading } = useChatbot();
  const { isTouchDevice } = useMobile();
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const enable = useCallback(async (): Promise<void> => {
    try {
      await setEnabled(true);
    } catch (error) {
      console.error('Failed to enable chatbot:', error);
    }
  }, [setEnabled]);

  // Touch-only: dismiss the revealed actions on outside tap or Escape.
  useEffect(() => {
    if (!expanded) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setExpanded(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setExpanded(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expanded]);

  const handleAudit = (): void => {
    // Dispatch first — the hidden ChatInterface records the pending request,
    // then auto-sends it once the assistant is enabled.
    dispatchChatbotHelp(
      'Audit my academic plan for correctness, completeness, and quality.',
      'audit_plan'
    );
    setExpanded(false);
    void enable();
  };

  const handleSuggest = (): void => {
    dispatchChatbotHelp(
      'Generate course suggestions for my plan.',
      'suggest_courses'
    );
    setExpanded(false);
    void enable();
  };

  // On touch the pill toggles the action group; on mouse/keyboard it keeps its
  // original one-click enable (the group reveals on hover/focus instead).
  const handlePillClick = (): void => {
    if (isTouchDevice) {
      setExpanded(prev => !prev);
      return;
    }
    void enable();
  };

  const pillLabel = (): string => {
    if (isLoading) return 'Enabling AI Assistant…';
    if (isTouchDevice) {
      return expanded ? 'Hide AI Assistant actions' : 'Show AI Assistant actions';
    }
    return 'Enable AI Assistant';
  };

  // Collapsed on touch: don't render the actions at all, so they can't take
  // focus or be announced while invisible. On mouse they must stay mounted —
  // that's what makes the focus-within reveal reachable by keyboard.
  const showActions = !isTouchDevice || expanded;

  return (
    <div
      ref={containerRef}
      className="group fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2 print:hidden"
      role="region"
      aria-label="AI Assistant (disabled)"
    >
      {showActions && (
        <div
          className={
            isTouchDevice
              ? `${REVEAL_BASE} ${REVEAL_VISIBLE}`
              : `${REVEAL_BASE} ${REVEAL_HIDDEN} ${REVEAL_ON_HOVER}`
          }
        >
          <button type="button" onClick={handleAudit} className={ACTION_BUTTON_CLASSES}>
            <ClipboardCheck className="h-3.5 w-3.5" aria-hidden />
            Audit Plan
          </button>
          <button type="button" onClick={handleSuggest} className={ACTION_BUTTON_CLASSES}>
            <Sparkles className="h-3.5 w-3.5 text-status-suggested" aria-hidden />
            Suggest Courses
          </button>
          {isTouchDevice && (
            <button
              type="button"
              onClick={() => void enable()}
              disabled={isLoading}
              className={`${ACTION_BUTTON_CLASSES} disabled:opacity-50`}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Enable AI Assistant
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handlePillClick}
        disabled={isLoading}
        aria-label={pillLabel()}
        title={pillLabel()}
        {...(isTouchDevice ? { 'aria-expanded': expanded } : {})}
        className={
          'flex h-12 w-12 items-center justify-center rounded-full shadow-lg ' +
          'bg-status-suggested text-white hover:bg-status-suggested/90 ' +
          'disabled:opacity-50 transition-all duration-base ease-out-soft ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-status-suggested/50 focus-visible:ring-offset-2'
        }
      >
        <Sparkles className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
};

export default ChatMinimizedPill;
